import { useState, useRef, useEffect } from 'react';
import { useUIStore } from '../../stores/ui';
import { useTenantStore } from '../../stores/tenant';
import { useLocation } from 'react-router-dom';
import styles from './AIPanel.module.css';
import { Button } from '../kumo';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function AIPanel() {
  const { aiPanelOpen, toggleAIPanel } = useUIStore();
  const { selectedTenantId } = useTenantStore();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (aiPanelOpen) inputRef.current?.focus();
  }, [aiPanelOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!aiPanelOpen) return null;

  async function sendMessage() {
    const msg = input.trim();
    if (!msg || loading) return;

    const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: msg, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          model_key: 'llama-3.3-70b',
          history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
          context: {
            tenant_id: selectedTenantId,
            page: location.pathname,
          },
        }),
      });

      const data = await res.json() as { success: boolean; data?: { response?: string }; error?: { message: string } };
      const reply = data.success ? data.data?.response ?? 'No response.' : `Error: ${data.error?.message}`;

      const assistantMsg: Message = { id: crypto.randomUUID(), role: 'assistant', content: reply, timestamp: new Date() };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg: Message = { id: crypto.randomUUID(), role: 'assistant', content: 'Failed to connect to AI service.', timestamp: new Date() };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>✨</span>
          <span className={styles.headerTitle}>NZT Copilot</span>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.clearBtn} onClick={() => setMessages([])} title="Clear conversation">
            🗑️
          </button>
          <button className={styles.closeBtn} onClick={toggleAIPanel} title="Close">
            ✕
          </button>
        </div>
      </div>

      <div className={styles.messages}>
        {messages.length === 0 && (
          <div className={styles.welcome}>
            <div className={styles.welcomeIcon}>✨</div>
            <p className={styles.welcomeTitle}>NZT Copilot</p>
            <p className={styles.welcomeText}>Ask me anything about your tenants, users, security posture, or network infrastructure.</p>
            <div className={styles.suggestions}>
              {['Which tenants have MFA gaps?', 'Summarize recent security alerts', 'Show users with risky sign-ins'].map(s => (
                <button key={s} className={styles.suggestion} onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={[styles.message, styles[msg.role]].join(' ')}>
            <div className={styles.messageBubble}>
              <pre className={styles.messageContent}>{msg.content}</pre>
            </div>
          </div>
        ))}
        {loading && (
          <div className={[styles.message, styles.assistant].join(' ')}>
            <div className={styles.messageBubble}>
              <div className={styles.typing}>
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputArea}>
        <textarea
          ref={inputRef}
          className={styles.textarea}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendMessage();
            }
          }}
          placeholder="Ask NZT Copilot… (Enter to send, Shift+Enter for newline)"
          rows={2}
          disabled={loading}
        />
        <Button variant="primary" size="sm" onClick={() => void sendMessage()} loading={loading} disabled={!input.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
}
